import React, { useState, useRef, useEffect } from 'react';
import { useGameContext } from '../GameContext';
import { checkGuess } from '../scrambleUtils';

const InputArea: React.FC = () => {
  const [guess, setGuess] = useState('');
  const [showIncorrectAnimation, setShowIncorrectAnimation] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Handle the animation timing
  useEffect(() => {
    if (showIncorrectAnimation) {
      const timer = setTimeout(() => {
        setShowIncorrectAnimation(false);
      }, 500); // Animation duration
      
      return () => clearTimeout(timer);
    }
  }, [showIncorrectAnimation]);
  const { 
    originalWords, 
    solvedIndices, 
    solveWord, 
    isLevelComplete, 
    isGameComplete,
    currentLevel,
    totalLevels,
    advanceToNextLevel,
    reshuffleWords
  } = useGameContext();
  
  const handleShuffle = (e: React.MouseEvent) => {
    // Prevent the default button behavior
    e.preventDefault();
    
    // Call the reshuffle function
    reshuffleWords();
    
    // Focus the input field
    if (inputRef.current) {
      // Use a longer timeout to ensure DOM fully updates before focusing
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Don't process form submissions if level is complete
    if (isLevelComplete()) {
      return;
    }
    
    // Don't show animation for empty guesses that come from automatic form submissions
    // This helps prevent the incorrect animation when pressing Enter to start the game
    if (!guess.trim() && document.activeElement !== inputRef.current) {
      return;
    }
    
    // If guess is empty and this is an active user input, treat as incorrect guess
    if (!guess.trim()) {
      setShowIncorrectAnimation(true);
      return;
    }
    
    const matchedIndex = checkGuess(guess, originalWords, solvedIndices);
    
    if (matchedIndex !== null) {
      solveWord(matchedIndex);
      setGuess('');
      // No success message needed
    } else {
      // Trigger the incorrect guess animation
      setShowIncorrectAnimation(true);
      setGuess(''); // Reset the input field when the guess is wrong
    }
  };

  // Choose a random celebration message
  const getCelebrationMessage = () => {
    const messages = [
      "You did it!",
      "Bravo!",
      "Fantastic!"
    ];
    const randomIndex = Math.floor(Math.random() * messages.length);
    return messages[randomIndex];
  };

  // Add event listener for Enter key when level is complete
  useEffect(() => {
    // Create a reference to the function for proper cleanup
    const handleKeyPress = (event: KeyboardEvent) => {
      // Only handle Enter key when we're actually showing the next level button
      if (event.key === 'Enter' && isLevelComplete()) {
        event.preventDefault(); // Prevent form submission
        event.stopPropagation(); // Stop event bubbling
        advanceToNextLevel();
      }
    };

    // Use capture phase to intercept the event before it reaches the form
    window.addEventListener('keydown', handleKeyPress, true);
    
    return () => {
      window.removeEventListener('keydown', handleKeyPress, true);
    };
  }, [isLevelComplete, advanceToNextLevel]);

  if (isGameComplete()) {
    return (
      <div className="input-area completed game-complete minimalist">
        <div className="completion-badge">{getCelebrationMessage()}</div>
      </div>
    );
  }

  if (isLevelComplete()) {
    return (
      <div className="input-area completed minimalist">
        {currentLevel < totalLevels ? (
          <button 
            onClick={advanceToNextLevel} 
            className="next-level-button"
          >
            Next Level
          </button>
        ) : (
          <button 
            onClick={advanceToNextLevel} 
            className="next-level-button"
          >
            Complete Game
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="input-area">
      <form onSubmit={handleSubmit}>
        <input
          type="text"
          value={guess}
          onChange={(e) => setGuess(e.target.value)}
          placeholder="Type a word..."
          autoFocus
          ref={inputRef}
          className={showIncorrectAnimation ? 'incorrect-guess' : ''}
        />
      </form>
      
      {/* Only show action buttons if there are unsolved words */}
      {solvedIndices.length < originalWords.length && (
        <div className="game-controls-container">
          <button 
            className="guess-button"
            onClick={handleSubmit}
            title="Submit your guess"
          >
            GUESS
          </button>
          
          <button 
            className="shuffle-button"
            onClick={handleShuffle}
            title="Rearrange scrambled letters"
          >
            SHUFFLE
          </button>
        </div>
      )}
    </div>
  );
};

export default InputArea;